/**
 * WhatsApp Trigger Service
 * 
 * Persistent trigger configs in Supabase + matching engine for:
 * - inbound_message (keywords / AI intent)
 * - ctwa_ad (Click-to-WhatsApp Ad referral)
 * - wa_url (WhatsApp URL with prefilled text)
 */

import { supabase } from '@/integrations/supabase/client';
import { logService, generateId, messageService } from './dataService';
import { getCurrentWorkspaceId } from './workspaceState';

// ===================== TYPES =====================

export type WaTriggerType = 'inbound_message' | 'ctwa_ad' | 'wa_url' | 'template_button_click';
export type KeywordMatchMode = 'contains' | 'exact' | 'starts_with';
export type WaUrlMatchMode = 'exact' | 'starts_with' | 'contains';
export type CtwaMatchBy = 'ad_id' | 'ad_account';

export interface InboundMessageConfig {
  mode: 'keywords' | 'intent';
  keywords: string[];
  keywordMatchMode: KeywordMatchMode;
  intentNames: string[];
  confidenceThreshold: number;
  requireTextOnly: boolean;
  requireFirstMessageOnly: boolean;
}

export interface CtwaAdConfig {
  matchBy: CtwaMatchBy;
  adId: string;
  adAccountId: string;
  requireReferral: boolean;
}

export interface WaUrlConfig {
  prefillText: string;
  matchMode: WaUrlMatchMode;
  campaignCode: string;
  requireFirstMessageOnly: boolean;
}

export interface TemplateButtonClickConfig {
  templateName: string;
  templateId?: string;
  buttonText: string;
  matchMode: 'exact' | 'contains';
}

export interface AutomationTrigger {
  id: string;
  automationId: string;
  channel: 'whatsapp';
  triggerType: WaTriggerType;
  isActive: boolean;
  config: InboundMessageConfig | CtwaAdConfig | WaUrlConfig | TemplateButtonClickConfig;
  createdAt: string;
  updatedAt: string;
}

export interface TriggerEventContext {
  contactId: string;
  conversationId: string;
  messageId: string;
  text: string;
  normalizedText: string;
  referral?: ReferralData;
  timestamp: string;
  isFirstMessage: boolean;
  messageType: string;
}

export interface ReferralData {
  source_url?: string;
  source_id?: string;
  source_type?: string;
  headline?: string;
  body?: string;
  media_type?: string;
  media_url?: string;
  ctwa_clid?: string;
  ad_id?: string;
  ad_account_id?: string;
}

export interface TriggerMatchResult {
  matched: boolean;
  triggerId: string;
  automationId: string;
  triggerType: WaTriggerType;
  matchedKeyword?: string;
  matchedIntent?: string;
  matchedIntentConfidence?: number;
  matchedAdId?: string;
  matchedCampaignCode?: string;
  diagnostics: Record<string, any>;
}

// ===================== LOCAL CACHE =====================
// Keep a local cache that syncs from DB for fast synchronous reads

function getCacheKey(): string {
  const wsId = getCurrentWorkspaceId();
  return `wf_automation_triggers_cache_${wsId}`;
}
let _cache: AutomationTrigger[] | null = null;
let _cacheWsId: string | null = null;

function getCache(): AutomationTrigger[] {
  const wsId = getCurrentWorkspaceId();
  if (_cache && _cacheWsId === wsId) return _cache;
  _cacheWsId = wsId;
  try { _cache = JSON.parse(localStorage.getItem(getCacheKey()) || '[]'); return _cache!; } catch { return []; }
}

function setCache(triggers: AutomationTrigger[]) {
  _cache = triggers;
  _cacheWsId = getCurrentWorkspaceId();
  localStorage.setItem(getCacheKey(), JSON.stringify(triggers));
}

/** Map DB row → AutomationTrigger */
function rowToTrigger(row: any): AutomationTrigger {
  return {
    id: row.id,
    automationId: row.automation_id,
    channel: row.channel || 'whatsapp',
    triggerType: row.trigger_type as WaTriggerType,
    isActive: row.is_active,
    config: typeof row.config === 'string' ? JSON.parse(row.config) : row.config,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ===================== SUPABASE CRUD SERVICE =====================

export const triggerService = {
  /** Sync cache from DB — call on app startup */
  syncFromDb: async (): Promise<AutomationTrigger[]> => {
    const { data, error } = await supabase
      .from('automation_triggers' as any)
      .select('*')
      .order('created_at', { ascending: true });

    if (error) {
      console.warn('triggerService.syncFromDb error:', error.message);
      return getCache();
    }

    const triggers = (data || []).map(rowToTrigger);
    setCache(triggers);
    return triggers;
  },

  getAll: (): AutomationTrigger[] => getCache(),

  getByAutomation: (automationId: string): AutomationTrigger | null =>
    getCache().find(t => t.automationId === automationId) || null,

  getActiveByType: (triggerType: WaTriggerType): AutomationTrigger[] =>
    getCache().filter(t => t.isActive && t.triggerType === triggerType),

  getAllActive: (): AutomationTrigger[] =>
    getCache().filter(t => t.isActive),

  save: async (data: Omit<AutomationTrigger, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }): Promise<AutomationTrigger> => {
    // Check if trigger already exists for this automation
    const existing = getCache().find(t => t.automationId === data.automationId);

    if (existing) {
      // Update
      const { data: updated, error } = await supabase
        .from('automation_triggers' as any)
        .update({
          trigger_type: data.triggerType,
          channel: data.channel,
          is_active: data.isActive,
          config: data.config as any,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        console.error('triggerService.save update error:', error.message);
        // Fallback: update cache directly
        const cache = getCache();
        const idx = cache.findIndex(t => t.id === existing.id);
        if (idx !== -1) {
          cache[idx] = { ...cache[idx], triggerType: data.triggerType, isActive: data.isActive, config: data.config, updatedAt: new Date().toISOString() };
          setCache(cache);
        }
        return cache[idx] || existing;
      }

      const trigger = rowToTrigger(updated);
      const cache = getCache().map(t => t.id === trigger.id ? trigger : t);
      setCache(cache);

      logService.add('triggers', 'trigger_updated', `Trigger ${trigger.triggerType} updated for automation ${data.automationId}`, {
        triggerId: trigger.id, automationId: data.automationId, triggerType: trigger.triggerType,
      }, 'info', 'trigger', trigger.id);

      return trigger;
    }

    // Insert new
    const { data: inserted, error } = await supabase
      .from('automation_triggers' as any)
      .insert({
        automation_id: data.automationId,
        channel: data.channel || 'whatsapp',
        trigger_type: data.triggerType,
        is_active: data.isActive,
        config: data.config as any,
        workspace_id: getCurrentWorkspaceId(),
      })
      .select()
      .single();

    if (error) {
      console.error('triggerService.save insert error:', error.message);
      // Fallback: save locally
      const trigger: AutomationTrigger = {
        id: data.id || generateId(),
        automationId: data.automationId,
        channel: 'whatsapp',
        triggerType: data.triggerType,
        isActive: data.isActive,
        config: data.config,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setCache([...getCache(), trigger]);
      return trigger;
    }

    const trigger = rowToTrigger(inserted);
    setCache([...getCache(), trigger]);

    logService.add('triggers', 'trigger_created', `Trigger ${trigger.triggerType} saved for automation ${data.automationId}`, {
      triggerId: trigger.id, automationId: data.automationId, triggerType: trigger.triggerType,
    }, 'info', 'trigger', trigger.id);

    return trigger;
  },

  delete: async (automationId: string) => {
    const existing = getCache().find(t => t.automationId === automationId);
    if (!existing) return;

    await supabase
      .from('automation_triggers' as any)
      .delete()
      .eq('automation_id', automationId);

    setCache(getCache().filter(t => t.automationId !== automationId));

    logService.add('triggers', 'trigger_deleted', `Trigger deleted for automation ${automationId}`, {
      triggerId: existing.id, automationId,
    }, 'info', 'trigger', existing.id);
  },

  setActive: async (automationId: string, isActive: boolean) => {
    const existing = getCache().find(t => t.automationId === automationId);
    if (!existing) return;

    await supabase
      .from('automation_triggers' as any)
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('automation_id', automationId);

    const cache = getCache();
    const idx = cache.findIndex(t => t.automationId === automationId);
    if (idx !== -1) {
      cache[idx].isActive = isActive;
      cache[idx].updatedAt = new Date().toISOString();
      setCache(cache);
    }
  },

  /** Record a trigger match/run in the DB for auditing */
  recordRun: async (match: TriggerMatchResult, ctx: TriggerEventContext, status: 'matched' | 'executed' | 'failed' | 'skipped' = 'matched') => {
    await supabase
      .from('automation_trigger_runs' as any)
      .insert({
        trigger_id: match.triggerId,
        automation_id: match.automationId,
        trigger_type: match.triggerType,
        contact_id: ctx.contactId,
        conversation_id: ctx.conversationId,
        message_id: ctx.messageId,
        matched_keyword: match.matchedKeyword || null,
        matched_intent: match.matchedIntent || null,
        matched_intent_confidence: match.matchedIntentConfidence || null,
        matched_ad_id: match.matchedAdId || null,
        matched_campaign_code: match.matchedCampaignCode || null,
        diagnostics: match.diagnostics,
        status,
        workspace_id: getCurrentWorkspaceId(),
      });
  },

  /** Get recent trigger runs for display */
  getRecentRuns: async (limit = 50): Promise<any[]> => {
    const { data } = await supabase
      .from('automation_trigger_runs' as any)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    return data || [];
  },
};

// ===================== HELPERS =====================

export function normalizeMessageText(text: string): string {
  return (text || '').toLowerCase().trim().replace(/\s+/g, ' ');
}

export function isFirstIncomingMessage(contactId: string, conversationId: string): boolean {
  const msgs = messageService.getByConversation(conversationId);
  const inbound = msgs.filter(m => m.direction === 'inbound');
  return inbound.length <= 1;
}

export function extractReferralMetadata(payload: any): ReferralData | undefined {
  const ref = payload?.referral || payload?.context?.referral;
  if (!ref) return undefined;

  return {
    source_url: ref.source_url,
    source_id: ref.source_id,
    source_type: ref.source_type,
    headline: ref.headline,
    body: ref.body,
    media_type: ref.media_type,
    media_url: ref.media_url,
    ctwa_clid: ref.ctwa_clid,
    ad_id: ref.source_id,
    ad_account_id: ref.ad_account_id,
  };
}

// ===================== MATCHERS =====================

export function matchInboundMessageTrigger(
  config: InboundMessageConfig,
  ctx: TriggerEventContext,
): { matched: boolean; matchedKeyword?: string; diagnostics: Record<string, any> } {
  const diagnostics: Record<string, any> = {
    mode: config.mode,
    normalizedText: ctx.normalizedText,
    isFirstMessage: ctx.isFirstMessage,
    messageType: ctx.messageType,
  };

  if (config.requireTextOnly && ctx.messageType !== 'text') {
    diagnostics.skipReason = 'requireTextOnly but message is ' + ctx.messageType;
    return { matched: false, diagnostics };
  }

  if (config.requireFirstMessageOnly && !ctx.isFirstMessage) {
    diagnostics.skipReason = 'requireFirstMessageOnly but not first';
    return { matched: false, diagnostics };
  }

  if (config.mode === 'keywords') {
    if (!config.keywords || config.keywords.length === 0) {
      diagnostics.skipReason = 'No keywords configured';
      return { matched: false, diagnostics };
    }

    for (const keyword of config.keywords) {
      const normalizedKw = normalizeMessageText(keyword);
      let matched = false;

      switch (config.keywordMatchMode) {
        case 'exact':
          matched = ctx.normalizedText === normalizedKw;
          break;
        case 'starts_with':
          matched = ctx.normalizedText.startsWith(normalizedKw);
          break;
        case 'contains':
        default:
          matched = ctx.normalizedText.includes(normalizedKw);
          break;
      }

      if (matched) {
        diagnostics.matchedKeyword = keyword;
        diagnostics.matchMode = config.keywordMatchMode;
        return { matched: true, matchedKeyword: keyword, diagnostics };
      }
    }

    diagnostics.skipReason = 'No keyword matched';
    diagnostics.testedKeywords = config.keywords;
    return { matched: false, diagnostics };
  }

  diagnostics.skipReason = 'Intent mode requires AI classification';
  return { matched: false, diagnostics };
}

export function matchIntentTrigger(
  config: InboundMessageConfig,
  intentResult: { intent: string; confidence: number },
): { matched: boolean; matchedIntent?: string; confidence?: number; diagnostics: Record<string, any> } {
  const diagnostics: Record<string, any> = {
    detectedIntent: intentResult.intent,
    detectedConfidence: intentResult.confidence,
    requiredIntents: config.intentNames,
    threshold: config.confidenceThreshold || 0.5,
  };

  if (!config.intentNames || config.intentNames.length === 0) {
    diagnostics.skipReason = 'No intents configured';
    return { matched: false, diagnostics };
  }

  const threshold = config.confidenceThreshold || 0.5;
  if (intentResult.confidence < threshold) {
    diagnostics.skipReason = `Confidence ${intentResult.confidence} below threshold ${threshold}`;
    return { matched: false, diagnostics };
  }

  if (config.intentNames.includes(intentResult.intent)) {
    return {
      matched: true,
      matchedIntent: intentResult.intent,
      confidence: intentResult.confidence,
      diagnostics,
    };
  }

  diagnostics.skipReason = `Intent "${intentResult.intent}" not in configured list`;
  return { matched: false, diagnostics };
}

export function matchCtwaTrigger(
  config: CtwaAdConfig,
  referral: ReferralData | undefined,
): { matched: boolean; matchedAdId?: string; diagnostics: Record<string, any> } {
  const diagnostics: Record<string, any> = {
    matchBy: config.matchBy,
    hasReferral: !!referral,
    referralData: referral || null,
  };

  if (!referral) {
    diagnostics.skipReason = 'No referral data in message';
    return { matched: false, diagnostics };
  }

  if (referral.source_type !== 'ad' && !referral.ctwa_clid) {
    diagnostics.skipReason = 'Referral source_type is not "ad"';
    return { matched: false, diagnostics };
  }

  if (config.matchBy === 'ad_id') {
    if (!config.adId) {
      diagnostics.skipReason = 'No ad_id configured';
      return { matched: false, diagnostics };
    }
    const match = referral.source_id === config.adId || referral.ad_id === config.adId;
    diagnostics.configuredAdId = config.adId;
    diagnostics.referralSourceId = referral.source_id;
    if (match) return { matched: true, matchedAdId: config.adId, diagnostics };
    diagnostics.skipReason = 'Ad ID mismatch';
    return { matched: false, diagnostics };
  }

  if (config.matchBy === 'ad_account') {
    if (!config.adAccountId) {
      diagnostics.skipReason = 'No ad_account_id configured';
      return { matched: false, diagnostics };
    }
    if (referral.ad_account_id === config.adAccountId) {
      return { matched: true, matchedAdId: config.adAccountId, diagnostics };
    }
    diagnostics.skipReason = 'Ad account ID mismatch or not available in referral';
    diagnostics.note = 'Ad account matching is limited — Meta does not always include ad_account_id in referral payload';
    return { matched: false, diagnostics };
  }

  return { matched: false, diagnostics };
}

export function matchWaUrlTrigger(
  config: WaUrlConfig,
  ctx: TriggerEventContext,
): { matched: boolean; matchedCampaignCode?: string; diagnostics: Record<string, any> } {
  const diagnostics: Record<string, any> = {
    prefillText: config.prefillText,
    matchMode: config.matchMode,
    normalizedText: ctx.normalizedText,
    isFirstMessage: ctx.isFirstMessage,
  };

  if (config.requireFirstMessageOnly && !ctx.isFirstMessage) {
    diagnostics.skipReason = 'requireFirstMessageOnly but not first message';
    return { matched: false, diagnostics };
  }

  if (!config.prefillText) {
    diagnostics.skipReason = 'No prefill text configured';
    return { matched: false, diagnostics };
  }

  const normalizedPrefill = normalizeMessageText(config.prefillText);
  let matched = false;

  switch (config.matchMode) {
    case 'exact':
      matched = ctx.normalizedText === normalizedPrefill;
      break;
    case 'starts_with':
      matched = ctx.normalizedText.startsWith(normalizedPrefill);
      break;
    case 'contains':
    default:
      matched = ctx.normalizedText.includes(normalizedPrefill);
      break;
  }

  if (matched) {
    diagnostics.matchedPrefill = config.prefillText;
    return {
      matched: true,
      matchedCampaignCode: config.campaignCode || undefined,
      diagnostics,
    };
  }

  diagnostics.skipReason = 'Prefill text not matched';
  return { matched: false, diagnostics };
}

// ===================== MAIN TRIGGER EVALUATION PIPELINE =====================

export interface EvaluationResult {
  matches: TriggerMatchResult[];
  diagnostics: Record<string, any>[];
}

/**
 * Evaluate all active WhatsApp triggers against an incoming event context.
 * Records matched triggers to DB for audit trail.
 */
export function evaluateWhatsAppTriggers(
  ctx: TriggerEventContext,
  intentResult?: { intent: string; confidence: number },
): EvaluationResult {
  const allTriggers = triggerService.getAllActive();
  const matches: TriggerMatchResult[] = [];
  const diagnostics: Record<string, any>[] = [];

  for (const trigger of allTriggers) {
    const base = {
      triggerId: trigger.id,
      automationId: trigger.automationId,
      triggerType: trigger.triggerType,
    };

    let result: TriggerMatchResult;

    switch (trigger.triggerType) {
      case 'inbound_message': {
        const cfg = trigger.config as InboundMessageConfig;
        if (cfg.mode === 'keywords') {
          const m = matchInboundMessageTrigger(cfg, ctx);
          result = { ...base, matched: m.matched, matchedKeyword: m.matchedKeyword, diagnostics: m.diagnostics };
        } else if (cfg.mode === 'intent' && intentResult) {
          const m = matchIntentTrigger(cfg, intentResult);
          result = { ...base, matched: m.matched, matchedIntent: m.matchedIntent, matchedIntentConfidence: m.confidence, diagnostics: m.diagnostics };
        } else {
          result = { ...base, matched: false, diagnostics: { skipReason: 'Intent mode but no intent result available' } };
        }
        break;
      }

      case 'ctwa_ad': {
        const cfg = trigger.config as CtwaAdConfig;
        const m = matchCtwaTrigger(cfg, ctx.referral);
        result = { ...base, matched: m.matched, matchedAdId: m.matchedAdId, diagnostics: m.diagnostics };
        break;
      }

      case 'wa_url': {
        const cfg = trigger.config as WaUrlConfig;
        const m = matchWaUrlTrigger(cfg, ctx);
        result = { ...base, matched: m.matched, matchedCampaignCode: m.matchedCampaignCode, diagnostics: m.diagnostics };
        break;
      }

      default:
        result = { ...base, matched: false, diagnostics: { skipReason: 'Unknown trigger type' } };
    }

    // Log trigger check
    const logLevel = result.matched ? 'success' : 'info';
    const logAction = result.matched ? 'trigger_matched' : 'trigger_not_matched';
    logService.add('triggers', logAction,
      result.matched
        ? `Trigger ${trigger.triggerType} matched for automation ${trigger.automationId}`
        : `Trigger ${trigger.triggerType} did not match`,
      {
        triggerId: trigger.id, automationId: trigger.automationId,
        triggerType: trigger.triggerType, contactId: ctx.contactId,
        conversationId: ctx.conversationId, messageId: ctx.messageId,
        ...result.diagnostics,
      }, logLevel, 'trigger', trigger.id);

    if (result.matched) {
      matches.push(result);
      // Record to DB asynchronously
      triggerService.recordRun(result, ctx, 'matched');
    }
    diagnostics.push({ triggerId: trigger.id, ...result.diagnostics });
  }

  return { matches, diagnostics };
}

// ===================== TESTING HELPERS =====================

export function testInboundMessageTrigger(
  config: InboundMessageConfig,
  sampleText: string,
): { matched: boolean; matchedKeyword?: string; diagnostics: Record<string, any> } {
  const ctx: TriggerEventContext = {
    contactId: 'test', conversationId: 'test', messageId: 'test',
    text: sampleText, normalizedText: normalizeMessageText(sampleText),
    timestamp: new Date().toISOString(), isFirstMessage: true, messageType: 'text',
  };
  return matchInboundMessageTrigger(config, ctx);
}

export function testCtwaTrigger(
  config: CtwaAdConfig,
  sampleReferral: ReferralData,
): { matched: boolean; diagnostics: Record<string, any> } {
  return matchCtwaTrigger(config, sampleReferral);
}

export function testWaUrlTrigger(
  config: WaUrlConfig,
  sampleText: string,
): { matched: boolean; diagnostics: Record<string, any> } {
  const ctx: TriggerEventContext = {
    contactId: 'test', conversationId: 'test', messageId: 'test',
    text: sampleText, normalizedText: normalizeMessageText(sampleText),
    timestamp: new Date().toISOString(), isFirstMessage: true, messageType: 'text',
  };
  return matchWaUrlTrigger(config, ctx);
}

// ===================== VALIDATION =====================

export function validateTriggerConfig(triggerType: WaTriggerType, config: any): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (triggerType === 'inbound_message') {
    const cfg = config as InboundMessageConfig;
    if (cfg.mode === 'keywords') {
      if (!cfg.keywords || cfg.keywords.length === 0) errors.push('At least one keyword is required');
      if (cfg.keywords?.some(k => !k.trim())) errors.push('Keywords cannot be empty');
    } else if (cfg.mode === 'intent') {
      if (!cfg.intentNames || cfg.intentNames.length === 0) errors.push('At least one intent must be selected');
    }
  } else if (triggerType === 'ctwa_ad') {
    const cfg = config as CtwaAdConfig;
    if (cfg.matchBy === 'ad_id' && !cfg.adId?.trim()) errors.push('Ad ID is required');
    if (cfg.matchBy === 'ad_account' && !cfg.adAccountId?.trim()) errors.push('Ad Account ID is required');
  } else if (triggerType === 'wa_url') {
    const cfg = config as WaUrlConfig;
    if (!cfg.prefillText?.trim()) errors.push('Prefill text is required');
  } else if (triggerType === 'template_button_click') {
    const cfg = config as TemplateButtonClickConfig;
    if (!cfg.templateName?.trim()) errors.push('Template name is required');
  }

  return { valid: errors.length === 0, errors };
}

// ===================== URL GENERATOR =====================

export function generateWhatsAppUrl(phoneNumber: string, prefillText: string): string {
  const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
  const encoded = encodeURIComponent(prefillText);
  return `https://wa.me/${cleanPhone}?text=${encoded}`;
}
